import {StyleSheet, css} from 'aphrodite';
import React, {ReactNode} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {GREEN_DARK, ORANGE_LIGHT} from 'ui-library/Colors';

type Variant = 'success' | 'warning';

interface AnchorOrigin {
  vertical?: 'top' | 'middle' | 'bottom';
  horizontal?: 'left' | 'middle' | 'right';
}
interface AnchorOffset {
  vertical?: number;
  horizontal?: number;
}

interface Props {
  /** Whether or not to hide the beacon. Defaults to false. */
  hide?: boolean;
  /** The color variant of the beacon. Defaults to 'success'. */
  variant?: Variant;
  /** The center position of the beacon relative to the children. Defaults to top-left. */
  anchorOrigin?: AnchorOrigin;
  /** The offset of the beacon relative to the anchor origin. Defaults to none. */
  anchorOffset?: AnchorOffset;
  /** The children to be wrapped by the badge. */
  children: ReactNode;
}

const SIZE_PX = 8;
const PERIOD_S = 2;
const COLOR_SUCCESS = GREEN_DARK;
const COLOR_WARNING = ORANGE_LIGHT;
const BADGE_TEST_PROPS = getTestProps({component: 'BeaconBadge', element: 'badge'});
const PULSE_TEST_PROPS = getTestProps({component: 'BeaconBadge', element: 'pulse'});

export function BeaconBadge({
  hide = false,
  variant = 'success',
  anchorOrigin = {},
  anchorOffset = {},
  children,
}: Props): JSX.Element {
  const variantStyle = VARIANT_STYLES[variant];
  const positionStyle = POSITION_STYLES[anchorOrigin.vertical ?? 'top'][anchorOrigin.horizontal ?? 'left'];
  return (
    <div className={css(styles.rootContainer)}>
      {children}
      {!hide && (
        <div
          role="presentation"
          className={css(styles.badgeContainer, positionStyle)}
          style={{margin: `${anchorOffset.vertical ?? 0}px ${anchorOffset.horizontal ?? 0}px`}}
        >
          <div className={css(styles.common, styles.badge, variantStyle.badge)} {...BADGE_TEST_PROPS} />
          <div className={css(styles.common, styles.pulse, variantStyle.pulse)} {...PULSE_TEST_PROPS} />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  rootContainer: {
    position: 'relative',
    display: 'inline-block',
  },
  badgeContainer: {
    position: 'absolute',
  },
  badgeContainerTopLeft: {
    top: 0,
    left: 0,
    transform: 'translate(-50%, -50%)',
  },
  badgeContainerTopMiddle: {
    top: 0,
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  badgeContainerTopRight: {
    top: 0,
    left: '100%',
    transform: 'translate(-50%, -50%)',
  },
  badgeContainerMiddleLeft: {
    top: '50%',
    left: 0,
    transform: 'translate(-50%, -50%)',
  },
  badgeContainerMiddleMiddle: {
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  badgeContainerMiddleRight: {
    top: '50%',
    left: '100%',
    transform: 'translate(-50%, -50%)',
  },
  badgeContainerBottomLeft: {
    top: '100%',
    left: 0,
    transform: 'translate(-50%, -50%)',
  },
  badgeContainerBottomMiddle: {
    top: '100%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  badgeContainerBottomRight: {
    top: '100%',
    left: '100%',
    transform: 'translate(-50%, -50%)',
  },
  common: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: `${SIZE_PX}px`,
    height: `${SIZE_PX}px`,
    borderRadius: '50%',
    animationDuration: `${PERIOD_S}s`,
    animationTimingFunction: 'ease-out',
    animationIterationCount: 'infinite',
  },
  badge: {
    animationName: {
      '0%': {
        transform: 'translate(50%, -50%) scale(1)',
      },
      '50%': {
        transform: 'translate(50%, -50%) scale(1.2)',
      },
      '100%': {
        transform: 'translate(50%, -50%) scale(1)',
      },
    },
  },
  badgeVariantSuccess: {
    backgroundColor: COLOR_SUCCESS,
  },
  badgeVariantWarning: {
    backgroundColor: COLOR_WARNING,
  },
  pulse: {
    backgroundColor: 'transparent',
  },
  pulseVariantSuccess: {
    animationName: {
      '0%': {
        transform: 'translate(50%, -50%) scale(0.2)',
        opacity: 0.4,
        boxShadow: `0 0 0 4px ${COLOR_SUCCESS}`,
      },
      '100%': {
        transform: 'translate(50%, -50%) scale(2)',
        opacity: 0.25,
        boxShadow: `0 0 0 0px ${COLOR_SUCCESS}`,
      },
    },
  },
  pulseVariantWarning: {
    animationName: {
      '0%': {
        transform: 'translate(50%, -50%) scale(0.2)',
        opacity: 0.4,
        boxShadow: `0 0 0 4px ${COLOR_WARNING}`,
      },
      '100%': {
        transform: 'translate(50%, -50%) scale(2)',
        opacity: 0.25,
        boxShadow: `0 0 0 0px ${COLOR_WARNING}`,
      },
    },
  },
});

const POSITION_STYLES = {
  top: {
    left: styles.badgeContainerTopLeft,
    middle: styles.badgeContainerTopMiddle,
    right: styles.badgeContainerTopRight,
  },
  middle: {
    left: styles.badgeContainerMiddleLeft,
    middle: styles.badgeContainerMiddleMiddle,
    right: styles.badgeContainerMiddleRight,
  },
  bottom: {
    left: styles.badgeContainerBottomLeft,
    middle: styles.badgeContainerBottomMiddle,
    right: styles.badgeContainerBottomRight,
  },
} as const;

const VARIANT_STYLES = {
  success: {
    badge: styles.badgeVariantSuccess,
    pulse: styles.pulseVariantSuccess,
  },
  warning: {
    badge: styles.badgeVariantWarning,
    pulse: styles.pulseVariantWarning,
  },
} as const;
